import Popcorn from "@/assets/icons/audience-fresh-popcorn.svg";
import Tomato from "@/assets/icons/critics-fresh-tomoato.svg";
import { Image } from "@/components/atoms/Image";
import { useClassNames } from "@/utils/classNames";

import styles from "./RottenTomatoesBadge.module.scss";
import { RottenTomatoesBadgeProps, RottenTomatoesBadgeVariant } from "./types";

/**
 * RottenTomatoesBadge component displays and Audience or Critics icon with a score percentage.
 *
 * @param variant - Visual style of the badge (Audience or Critics Score)
 * @param size - Size of the badge
 * @param score - The numeric percentage to display
 * @param className - Additional CSS class for styling
 * @param attr - Additional HTML attributes to pass to the rendered element
 *
 * @returns the rendered RottenTomatoesBadge component
 *
 * @example
 * ```tsx
 * <RottenTomatoesBadge
 *  variant={RottenTomatoesBadgeVariant.AudienceScore}
 *  size={RottenTomatoesBadgeSize.Large}
 *  score={75}
 * />
 * ```
 */
const RottenTomatoesBadge = ({
  variant = RottenTomatoesBadgeVariant.CriticsScore,
  size,
  score,
  className,
  ...attr
}: RottenTomatoesBadgeProps) => {
  const cn = useClassNames(
    styles,
    styles["rotten-tomatoes-badge"],
    styles[`size-${size}`],
    className,
  );

  const imgSrc =
    variant === RottenTomatoesBadgeVariant.AudienceScore ? Popcorn : Tomato;

  return (
    <div className={cn} {...attr}>
      <Image
        src={imgSrc}
        className={styles["icon"]}
        allowColorOverride={false}
      />
      {`${score}%`}
    </div>
  );
};

export { RottenTomatoesBadge };
